import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Badge,
  Tab,
  Tabs,
  Modal
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminLogsAPI, bookingInvoices, bookingsAPI, driversCompanyAPI } from '../../api/apiClient';
import { useAuth } from "../../context/AuthContext";
import "../../assets/css/OrderDetails.css";
import Swal from 'sweetalert2';
import { FaFacebookMessenger, FaEye, FaEyeSlash, FaEuroSign, FaCheck, FaTimes, FaBolt, FaTrashAlt, FaArrowLeft, FaMoneyBillWave, FaCog, FaFileInvoice, FaTruck, FaSearch, FaInfoCircle, FaJediOrder, FaLocationArrow, FaUserAlt, FaDownload, FaEdit, FaTimesCircle } from "react-icons/fa";
import { HiBoltSlash } from "react-icons/hi2";
import { TiThList } from "react-icons/ti";
import { formatDateTime, formatDate, formatTime, formatNumber } from '../../utils/helpers';
import { RiUserLocationFill } from "react-icons/ri";

export default function BookingDetails() {
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const highlightId = location.state?.highlightId;
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [discount, setDiscount] = useState(0);
  const [notizen, setMessage] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [showDeleteLog, setShowDeleteLog] = useState(false);
  const [deleteLogId, setDeleteLogId] = useState(null);
  const fromPage = location.state?.fromPage || 1;
  const { isSuperAdmin, checkSuperAdminStatus } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [showDriversModal, setShowDriversModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sendSms, setSendSms] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);


  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [form, setForm] = useState({
    invoice_num: '',
    customer_num: '',
    payment_term: '',
    created_at: '',
  });
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [showCancelInvoiceModal, setShowCancelInvoiceModal] = useState(false);
  const [cancelInvoiceId, setCancelInvoiceId] = useState(null);
  const [togglingStatus, setTogglingStatus] = useState({});

  useEffect(() => {
    fetchBookingDetails();
    fetchDrivers();
    checkSuperAdminStatus();
  }, [id]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await bookingsAPI.show(id);
      
      if (response.success) {
        setBooking(response.data.data || response.data);
        setDiscount(response.data.data?.discount_percentage || 0);
        setMessage(response.data.data?.notizen || '');
      } else {
        setError(response.message || t('failed_to_fetch_booking_details'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_booking_details'));
      console.error('Error fetching booking details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async (search = searchTerm) => {
    try {
      setLoading(true);
      const response = await driversCompanyAPI.index({
        search,
        per_page: 100 // Fetch a reasonable amount for the modal
      });
      if (response.success) {
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);
        setDrivers(response.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Debounced Search Effect for Drivers
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm !== undefined && showDriversModal) {
        fetchDrivers(searchTerm);
      }
    }, 1500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, showDriversModal]);

  // 2. إضافة جميع الـ API functions
  const handleSetPending = async () => {
    try {
      setUpdating(true);
      const response = await bookingsAPI.setPending(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("set_to_pending_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_status"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSetPendingOrder = async () => {
    try {
      setUpdating(true);
      const response = await bookingsAPI.setPendingOrder(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("set_to_pending_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_status"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleApprove = async () => {
    try {
      setUpdating(true);
      
      if (sendSms && sendEmail) {
        await bookingsAPI.approve(id, { send_sms: 1, send_email: 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else if (sendSms) {
        await bookingsAPI.approve(id, { send_sms: 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else if (sendEmail) {
        await bookingsAPI.approve(id, { send_email: 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        await bookingsAPI.approve(id);
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
      }
      
      fetchBookingDetails();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_approve"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleApproveSms = async () => {
    try {
      setUpdating(true);
      const response = await bookingsAPI.approveSms(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_approve"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleApproveEmail = async () => {
    try {
      setUpdating(true);
      const response = await bookingsAPI.approveEmail(id);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_approve"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      Swal.fire({
        icon: "warning",
        title: t("warning"),
        text: t("booking.please_enter_reject_reason"),
        timer: 3000,
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await bookingsAPI.reject(id, { rejected_reason: rejectReason });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.rejected_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        setShowRejectModal(false);
        setRejectReason('');
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_reject"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSetFastOrder = async () => {
    try {
      setUpdating(true);

      // const response = await bookingsAPI.setFastOrder(id, { fast_order: booking.fast_order ? 0 : 1 });
      // if (response.success) {
      //   Swal.fire({
      //     icon: "success",
      //     title: t("success"),
      //     text: t(booking.fast_order ? "booking.set_regular_booking_success" : "booking.set_fast_booking_success"),
      //     timer: 3000,
      //     showConfirmButton: false,
      //   });
      //   fetchBookingDetails();
      // } else {
      //   throw new Error(response.message);
      // }

      if (sendSms && sendEmail) {
        await bookingsAPI.setFastOrder(id, { send_sms: 1, send_email: 1, fast_order: booking.fast_order ? 0 : 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else if (sendSms) {
        await bookingsAPI.setFastOrder(id, { send_sms: 1, fast_order: booking.fast_order ? 0 : 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_sms_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else if (sendEmail) {
        await bookingsAPI.setFastOrder(id, { send_email: 1, fast_order: booking.fast_order ? 0 : 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.approved_email_sent"),
          timer: 3000,
          showConfirmButton: false,
        });
      } else {
        await bookingsAPI.setFastOrder(id, { fast_order: booking.fast_order ? 0 : 1 });
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.set_fast_booking_success"),
          timer: 3000,
          showConfirmButton: false,
        });
      }
      
      fetchBookingDetails();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_set_fast_booking"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkPaid = async () => {
    Swal.fire({
      title: t("confirm_action"),
      text: t("confirm_mark_paid"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("yes_mark_paid"),
      cancelButtonText: t("cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setUpdating(true);
          const response = await bookingsAPI.markPaid(id);
          
          if (response.success) {
            Swal.fire({
              icon: "success",
              title: t("success"),
              text: t("booking.marked_paid_successfully"),
              timer: 3000,
              showConfirmButton: false,
            });
            fetchBookingDetails();
          } else {
            throw new Error(response.message);
          }
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: err.message || t("failed_to_mark_paid"),
            timer: 3000,
          });
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  const handleHidePrice = async () => {
    try {
      setUpdating(true);
      console.log(booking.hide_price);
      const response = await bookingsAPI.hidePrice(id, { is_hide: booking.hide_price ? 0 : 1 });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t(booking.hide_price ? "booking.price_shown_successfully" : "booking.price_hidden_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_toggle_price"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  // 7. إصلاح مشكلة الـ Discount
  const handleUpdateDiscount = async () => {
    try {
      if (discount < 0 || discount > 100) {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("booking.discount_range_error"),
          timer: 3000,
        });
        return;
      }

      setUpdating(true);
      const response = await bookingsAPI.addDiscount(id, { 
        discount: discount 
      });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.discount_updated_successfully"), 
          timer: 3000,
          showConfirmButton: false,
        });
        
        fetchBookingDetails();
        
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_update_discount"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleSendMessage = async () => {
    try {
      const response = await bookingsAPI.sendNote(id, { notizen });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("booking.notes_submitted"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_send_note"),
        timer: 3000,
      });
    }
  };

  const handleDeleteBooking = async () => {
    const result = await Swal.fire({
      title: t('are_you_sure'),
      text: t('confirm_delete_booking'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('delete'),
      cancelButtonText: t('cancel')
    });

    if (result.isConfirmed) {
      try {
        const response = await bookingsAPI.delete(id);
        
        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: t('deleted'),
            text: t('booking_deleted_successfully'),
            timer: 3000,
            showConfirmButton: false
          });
          navigate('/admin/bookings');
        } else {
          throw new Error(response.message);
        }
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: err.message || t('failed_to_delete_booking'),
          timer: 3000,
        });
      }
    }
  };

  const openForm = (entry = null) => {
    if (entry) {
      setForm({
        invoice_num: entry.invoice_num || '',
        customer_num: entry.customer_num || '',
        payment_term: entry.payment_term || '',
        created_at: entry.created_at ? entry.created_at.replace(' ', 'T').substring(0, 16) : '',
      });
      setEditData(entry);
    } else {
      setForm({
        invoice_num: '',
        customer_num: '',
        payment_term: '',
        created_at: '',
      });
      setEditData(null);
    }
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      const fd = new FormData();
      
      fd.append('invoice_num', form.invoice_num);
      fd.append('customer_num', form.customer_num);
      fd.append('payment_term', form.payment_term);
      fd.append('created_at', form.created_at);

      let response;
      if (editData) {
        response = await bookingInvoices.update(editData.id, fd);
      } else {
        response = await bookingInvoices.store(fd);
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('data_saved_successfully'),
          timer: 2000,
          showConfirmButton: false
        });
        setShowForm(false);
        fetchBookingDetails();
      } else {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: response.message || t('failed_to_save_data'),
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('failed_to_save_data'),
        timer: 2000,
        showConfirmButton: false
      });
      console.error('Error saving data:', err);
    } finally {
      setLoading(false);
    }
  };

  const safeDisplay = (value) => {
    if (value === null || value === undefined) return t('not_available');
    if (typeof value === 'object') {
      console.warn('Trying to display object as text:', value);
      return JSON.stringify(value);
    }
    return value;
  };

  const formatCurrency = (amount, currency = "€") => {
    if (!amount) return `0,00 ${currency}`;
    
    if (typeof amount === 'object') {
      console.warn('Amount is an object:', amount);
      amount = amount.toString();
    }
    
    const cleanAmount = amount.toString().replace(",", ".");
    const number = parseFloat(cleanAmount);

    if (isNaN(number)) return `${amount} ${currency}`;

    return `${number.toFixed(2).replace(".", ",")} ${currency}`;
  };

  const handleDownload = async (invoice) => {
    setDownloading(true);
    setDownloadingId(invoice.id);
    try {

      if (!invoice.invoice_path) {
        Swal.fire({
          icon: 'error',
          title: t('error'),
          text: t('no_invoice_available'),
          timer: 2000,
          showConfirmButton: false
        });
        return;
      }

      const link = document.createElement("a");
      link.href = invoice.invoice_path;
      link.target = "_blank";
      link.download = `invoice-${invoice.booking_id || invoice.id}.pdf`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: t('download_failed'),
        timer: 2000,
        showConfirmButton: false
      });
    } finally {
      setDownloading(false);
      setDownloadingId(null);
    }
  };

  const handleView = (invoice) => {
    console.log("👁️ Viewing invoice:", invoice);
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleCancelInvoiceClick = (id) => {
    setCancelInvoiceId(id);
    setShowCancelInvoiceModal(true);
  };

  const handleCancelInvoiceConfirm = async () => {
    try {
      setLoading(true);
      const response = await bookingInvoices.cancel(cancelInvoiceId);
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("invoice_canceled_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_cancel_invoice"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      console.error("Error canceling invoice:", err);
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_cancel_invoice"),
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
      setShowCancelInvoiceModal(false);
      setCancelInvoiceId(null);
    }
  };

  const toggleStatus = async (entry) => {
    if (togglingStatus[entry]) return;

    try {
      setTogglingStatus(prev => ({ ...prev, [entry]: true }));

      const response = await bookingsAPI.toggleStatus(entry);
      if (response.success) {
        // ✅ تحديث البيانات محلياً
        const fetchedData = response.data.data || response.data;
        setData(fetchedData);

        Swal.fire({
          icon: 'success',
          title: t('success'),
          text: t('booking.status_updated_successfully'),
          timer: 2000,
          showConfirmButton: false,
        });

        await fetchBookingDetails();
      } else {
        throw new Error(response.message || t('failed_to_update_status'));
      }
    } catch (err) {
      console.error('❌ Error toggling status:', err);
      Swal.fire({
        icon: 'error',
        title: t('error'),
        text: err.message || t('failed_to_update_status'),
        timer: 3000,
      });
    } finally {
      setTogglingStatus(prev => ({ ...prev, [entry]: false }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // const formatDateTime = (dateTimeArray) => {
  //   if (!dateTimeArray || !dateTimeArray[0]) return '-';
  //   return dateTimeArray[0];
  // };

  const handleBack = () => {
    const state = {
      fromPage: location.state?.fromPage || 1,
      currentPage: location.state?.currentPage || 1,
      highlightId: location.state?.highlightId || parseInt(id),
      scrollToHighlight: true,
      activeTab: location.state?.activeTab || 'active'
    };
    
    console.log('Navigating back with state:', state);
    
    navigate('/admin/bookings', { 
      state: state
    });
  };

  const handleViewTrack = (booking_id) => {
    navigate(`/admin/bookings/track/${booking_id}`);
  };

  const handleViewLiveTracking = (id) => {
    navigate(`/admin/bookings/live-tracking/${id}`);
  };

  const handleViewOrder = (id) => {
    navigate(`/admin/booking-orders/orders-for-booking/${id}`);
  };

  const selectDriver = (driver) => {
    setUpdating(true);
    setSelectedDriver(driver);
    setShowDriversModal(false);
    handleAssignDriver(driver.id);
  };

  const handleAssignDriver = async (customerId) => {
    if (!customerId) {
      Swal.fire({
        icon: "warning",
        title: t("warning"),
        text: t("please_select_driver_first"),
        timer: 3000,
      });
      return;
    }

    try {
      setUpdating(true);
      const response = await bookingsAPI.assignDriver(id, { customer_id: customerId });
      
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("driver_assigned_successfully"),
          timer: 3000,
          showConfirmButton: false,
        });
        fetchBookingDetails();
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.message || t("failed_to_assign_driver"),
        timer: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const confirmDeleteLog = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminLogsAPI.delete(deleteLogId);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("data_deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowDeleteLog(false);
        fetchBookingDetails();
      } else {
        setError(response.message || t("failed_to_delete_data"));
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_delete_data"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      setError(t("failed_to_delete_data"));
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_delete_data"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error deleting log:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
        cancelled: { variant: 'danger', text: t('cancelled') },
        rejected: { variant: 'danger', text: t('rejected') },
        assigned: { variant: 'info', text: t('assigned') },
        received: { variant: 'secondary', text: t('received') },
        completed: { variant: 'success', text: t('completed') },
        pending: { variant: 'warning', text: t('pending') },
        approved: { variant: 'success', text: t('approved') }
      };
    
    const config = statusConfig[status] || { variant: 'light', text: status };
    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const renderAdditionalDetails = () => {
    if (!booking) return null;

    return (
      <>
        {/* معلومات الفاتورة */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="py-3 fw-bold card-title d-flex justify-content-between align-items-center">
            {t("booking.invoice_info")}
             {booking.invoice_path && (
              <div className="d-flex gap-2">
                <Button 
                  className="btn-light"
                  size="sm"
                  onClick={() => {
                    const url = `${booking.invoice_path}?t=${Date.now()}`;
                    window.open(url, '_blank', 'noopener,noreferrer');
                  }}
                >
                  <FaFileInvoice className="me-2" />
                  {t("booking.view_invoice")}
                </Button>
                {booking.bill_path && booking.bill_path.map((billUrl, index) => (
                  <>
                    <Button 
                      variant="info" 
                      size="sm"
                      onClick={() => window.open(billUrl, '_blank')}
                    >
                      <FaFileInvoice className="me-2" />
                      {t("booking.view_bill")} {index + 1}
                    </Button>
                  </>
                ))}
              </div>
            )}
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p><strong>{t("booking.invoice_company")}:</strong> {booking.invoice_company || '-'}</p>
                <p><strong>{t("booking.invoice_name")}:</strong> {booking.invoice_title} {booking.invoice_first_name} {booking.invoice_last_name}</p>
                <p><strong>{t("booking.invoice_email")}:</strong> {booking.invoice_email || '-'}</p>
                {/* <p><strong>{t("booking.invoice_email_2")}:</strong> {booking.invoice_email_2 || '-'}</p> */}
                <p><strong>{t("booking.invoice_phone")}:</strong> {booking.invocie_phone_number || '-'}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("booking.invoice_address")}:</strong> {booking.invoice_street_no || '-'}</p>
                <p><strong>{t("booking.invoice_additional")}:</strong> {booking.invoice_addition_address || '-'}</p>
                <p><strong>{t("booking.invoice_postal")}:</strong> {booking.invoice_postal_code || '-'}</p>
                <p><strong>{t("booking.invoice_reference")}:</strong> {booking.invoice_reffernce || '-'}</p>
              </Col>
              <hr />
              <Col md={12}>
                <div className="d-flex justify-content-center gap-3 align-items-center">

                  {/* toggle Status incoice */}
                  <div className="toggleWrapper">
                    <Form.Check
                      type="switch"
                      id={`approved-${booking.id}`}
                      checked={booking.show_invoice}
                      onChange={() => toggleStatus(booking.id)}
                      disabled={togglingStatus[booking.id]}
                      className="custom-switch"
                    />
                    {togglingStatus[booking.id] && (
                      <Spinner animation="border" size="sm" className="ms-2" />
                    )}
                  </div>

                  {/* زر التعديل */}
                  <Button
                    className="add-btn"
                    onClick={() => openForm(booking)}
                    title={t("edit")}
                    disabled={loading}
                  >
                    <FaEdit />
                  </Button>
                  
                  {/* زر تحميل الفاتورة */}
                  <Button
                    style={{
                      backgroundColor: "#ff7b00",
                      border: "none",
                      borderRadius: "20px",
                      padding: "5px 12px",
                    }}
                    onClick={() => handleDownload(booking)}
                    disabled={downloading && downloadingId === booking.id || !booking.invoice_path}
                    title={t("booking.download_pdf")}
                  >
                    {downloading && downloadingId === booking.id ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <FaDownload />
                    )}
                  </Button>
                  
                  {/* زر عرض الفاتورة */}
                  <Button
                    style={{
                      backgroundColor: "#28a745",
                      border: "none",
                      borderRadius: "20px",
                      padding: "5px 12px",
                    }}
                    onClick={() => handleView(booking)}
                    disabled={!booking.invoice_path}
                    title={t("viewInvoice")}
                  >
                    <FaEye />
                  </Button>

                  {/* زر إلغاء الفاتورة */}
                  {booking.invoice_status !== 'canceled' && (
                    <Button
                      size="sm"
                      variant="warning"
                      style={{
                        borderRadius: "20px",
                        padding: "5px 12px",
                        color: "white"
                      }}
                      onClick={() => handleCancelInvoiceClick(booking.id)}
                      title={t("cancel_invoice")}
                    >
                      <FaTimesCircle />
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* معلومات الدفع */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="fw-bold card-title">{t("booking.payment_info")}</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p><strong>{t("booking.payment_status")}:</strong> 
                  <Badge bg={booking.payment_status ? 'success' : 'warning'} className="ms-2">
                    {t(booking.payment) || t('unpaid')}
                  </Badge>
                </p>
                <p><strong>{t("booking.payment_method")}:</strong> {booking.payment_method || '-'}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("booking.fast_booking")}:</strong> 
                  <Badge bg={booking.fast_order ? 'success' : 'secondary'} className="ms-2">
                    {booking.fast_order ? t('yes') : t('no')}
                  </Badge>
                </p>
                <p><strong>{t("booking.price_visibility")}:</strong> 
                  <Badge bg={booking.hide_price ? 'secondary' : 'info'} className="ms-2">
                    {booking.hide_price ? t('hidden') : t('visible')}
                  </Badge>
                </p>
              </Col>
            </Row>
            {booking.rejected_reason && (
              <div className="mt-3 p-3 bg-light rounded">
                <strong>{t("booking.rejection_reason")}:</strong> {booking.rejected_reason}
              </div>
            )}
          </Card.Body>
        </Card>
      </>
    );
  };

  if (loading) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <Spinner animation="border" />
        <span className="ms-2">{t('loading')}</span>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="danger" className="m-3">
          {error}
          <Button variant="outline-danger" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!booking) {
    return (
      <Container fluid className="p-1 bg-light min-vh-100">
        <Alert variant="warning" className="m-3">
          {t('booking_not_found')}
          <Button variant="outline-warning" className="ms-3" onClick={handleBack}>
            {t('back_to_list')}
          </Button>
        </Alert>
      </Container>
    );
  }

  const clearSearch = () => {
    setSearchTerm('');
  };
  
  return (
    <Container fluid className="p-1 bg-light min-vh-100">

      {/* <Card className="mb-4 shadow-sm card-header">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div className="card-title">
            <h3 className="mb-1 fw-bold">{t("booking.check_data")}</h3>
            <small>{t("booking.overview")}</small>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {booking.status === 'pending' && (
              <>
                <Button 
                  variant="success" 
                  onClick={handleApprove}
                  disabled={updating}
                >
                  <FaCheck className="me-1" /> {t("booking.approve")}
                </Button>
                <Button 
                  variant="danger" 
                  onClick={() => setShowRejectModal(true)}
                  disabled={updating}
                >
                  <FaTimes className="me-1" /> {t("booking.reject")}
                </Button>
              </>
            )}
            
            {booking.status === 'approved' && (
              <Button 
                variant="warning" 
                onClick={handleSetPending}
                disabled={updating}
              >
                {t("booking.set_pending")}
              </Button>
            )}

            <Button 
              variant={booking.fast_order ? "success" : "outline-success"}
              onClick={handleSetFastOrder}
              disabled={updating}
            >
              <FaBolt className="me-1" /> {t("booking.fast_order")}
            </Button>

            {booking.payment_status === 0 && (
              <Button 
                variant={booking.payment_status ? "success" : "outline-success"}
                onClick={handleMarkPaid}
                disabled={updating || booking.payment_status}
              >
                <FaEuroSign className="me-1" /> {t("booking.mark_paid")}
              </Button>
            )}

            <Button variant="secondary" onClick={handleBack}>
              {t("booking.back")}
            </Button>
            <Button variant="outline-danger" onClick={handleDeleteBooking}>
              {t("delete")}
            </Button>
          </div>
        </Card.Body>
      </Card> */}

      <Card className="mb-4 shadow-sm card-header">
        <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <div className="card-title">
            <h2 className="mb-0">
              {t("booking.details")} - <span className="text-primary">{booking.booking_id}</span>
            </h2>
            <small>{t("booking.overview")}</small>
          </div>
          
          <div className="d-flex flex-wrap gap-2">
              {getStatusBadge(booking.status || 'pending')}

              <Badge bg={booking.payment_status ? 'success' : 'warning'} className="ms-2">
                {t(booking.payment) || t('unpaid')}
              </Badge>

              <small className="text-white">
                {t('booking.created_on')}: {formatDateTime(booking.created_on)}
              </small>
          </div>
        </Card.Body>
      </Card>

      {(booking.status === 'cancelled' || booking.status === 'canceled' || booking.deleted_reason) && (
        <Alert variant="danger" className="mb-4">
          <strong>{t("cancellation_reason")}:</strong>{" "}
          {booking.deleted_reason || t("no_cancellation_reason")}
        </Alert>
      )}

      {/* Actions Section */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-bold card-title d-flex align-items-center">
          <FaCog className="me-2" />
          {t("booking.actions_section")}
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <h6 className="fw-bold mb-3 text-primary">
              <FaCog className="me-2" />
              {t("booking.status_management")}
              </h6>

              {/* Counters / Compact design for counts */}
              {/* <div className="d-flex flex-wrap gap-2 mb-3">
                <div className="p-2 bg-white border rounded text-center" style={{ minWidth: 110 }}>
                  <div className="small text-muted">{t('loading_location')}</div>
                  <Badge bg="info" pill aria-label={t('loading_location')}>
                  {booking.booking_from?.length || 0}
                  </Badge>
                </div>

                <div className="p-2 bg-white border rounded text-center" style={{ minWidth: 110 }}>
                  <div className="small text-muted">{t('unloading_location')}</div>
                  <Badge bg="primary" pill aria-label={t('unloading_location')}>
                  {booking.booking_to?.length || 0}
                  </Badge>
                </div>

                <div className="p-2 bg-white border rounded text-center" style={{ minWidth: 110 }}>
                  <div className="small text-muted">{t('orders')}</div>
                  <Badge bg="success" pill aria-label={t('booking.orders')}>
                  {booking.order_count || 0}
                  </Badge>
                </div>

                <div className="p-2 bg-white border rounded text-center" style={{ minWidth: 110 }}>
                  <div className="small text-muted">{t('admin_logs')}</div>
                  <Badge bg="warning" pill aria-label={t('admin_logs')}>
                  {booking.admin_logs?.length || 0}
                  </Badge>
                </div>
              </div> */}

              {/* Action buttons */}
              <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                {booking.status === 'pending' && (
                  <>
                    <Form.Check
                      type="switch"
                      id="send-sms-switch"
                      label={t("booking.send_sms")}
                      checked={sendSms}
                      onChange={(e) => setSendSms(e.target.checked)}
                      className="me-3"
                    />
                    <Form.Check
                      type="switch"
                      id="send-email-switch"
                      label={t("booking.send_email")}
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="me-3"
                    />
                    <Button
                      variant="success"
                      onClick={handleApprove}
                      disabled={updating}
                      size="sm"
                    >
                      <FaCheck className="me-1" /> {t("booking.approve")}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setShowRejectModal(true)}
                      disabled={updating}
                      size="sm"
                    >
                      <FaTimes className="me-1" /> {t("booking.reject")}
                    </Button>
                  </>
                )}

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewTrack(booking.booking_id)}
                  className="mx-1"
                >
                  <FaTruck /> {t("track")}
                </Button>

                {booking.order && booking.status !== 'completed' && (
                  <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewLiveTracking(booking.order.id)}
                  className="mx-1"
                  >
                  <FaLocationArrow /> {t("live_tracking")}
                  </Button>
                )}

                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => handleViewOrder(booking.id)}
                  className="mx-1 d-flex align-items-center"
                >
                  <TiThList />
                  <span className="ms-2">{t("booking.orders")}</span>
                  <Badge bg="secondary" pill className="ms-2">
                    {booking.order_count || 0}
                  </Badge>
                </Button>

                {booking.status === 'approved' && (
                  <Button
                  variant="warning"
                  onClick={handleSetPending}
                  disabled={updating}
                  size="sm"
                  >
                  {t("booking.set_pending")}
                  </Button>
                )}

                {['assigned', 'received', 'completed'].includes(booking.status) && (
                  <Button
                  variant="warning"
                  onClick={handleSetPendingOrder}
                  disabled={updating}
                  size="sm"
                  >
                  {t("booking.set_pending_order")}
                  </Button>
                )}
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <h6 className="fw-bold mb-3 text-success">
                <FaMoneyBillWave className="me-2" />
                {t("booking.payment_management")}
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {!booking.order && (
                  <Button 
                    variant={booking.fast_order ? "outline-success" : "success"}
                    onClick={handleSetFastOrder}
                    disabled={updating}
                    size="sm"
                  >
                    {booking.fast_order ? <HiBoltSlash className="me-1" /> : <FaBolt className="me-1" />}
                    {booking.fast_order ? t("booking.regular_booking") : t("booking.fast_booking")}
                  </Button>
                )}

                {(booking.payment_status !== "Bezahlt" && booking.payment_status !== "Paid" && booking.invoice_status !== "canceled") && (
                  <Button 
                    variant={booking.payment_status ? "success" : "outline-primary"}
                    onClick={handleMarkPaid}
                    disabled={updating || booking.payment_status}
                    size="sm"
                  >
                    <FaEuroSign className="me-1" /> {t("booking.mark_paid")}
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {/* أزرار التنقل والإجراءات الخطرة */}
          <Row className="mt-3 pt-3 border-top">
            <Col className="d-flex flex-wrap gap-2 justify-content-between">
              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-secondary" onClick={handleBack} size="sm">
                  <FaArrowLeft className="me-1" />
                  {t("booking.back")}
                </Button>
                
                <Button 
                  variant="outline-danger" 
                  onClick={handleDeleteBooking}
                  size="sm"
                >
                  <FaTrashAlt className="me-1" />
                  {t("delete")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" fill>
        <Tab eventKey="details" title={t("booking_details")}>
          <Row>
            <Col md={8}>

              {/* معلومات التحميل */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  {t("booking.loading_info")} ({booking.booking_from?.length || 0} {t('locations')})
                </Card.Header>
                <Card.Body>
                  {booking.booking_from?.map((address, index) => (
                    <div key={index} className="border p-3 mb-3">
                      <h6>{t('loading_location')} {index + 1}:</h6>
                      <p><strong>{t("booking.company")}:</strong> {booking.loading_company?.[index] || '-'}</p>
                      <p><strong>{t("booking.name")}:</strong> {booking.loading_first_name?.[index]} {booking.loading_last_name?.[index]}</p>
                      <p><strong>{t("booking.address")}:</strong> {booking.loading_streat_no?.[index]}, {address}</p>
                      <p><strong>{t("booking.loading_address_additional")}:</strong> {booking.loading_address_additional?.[index]}</p>
                      <p><strong>{t("booking.loading_postal")}:</strong> {booking.loading_postal?.[index]}</p>
                      <p><strong>{t("booking.phone")}:</strong> {booking.loading_phone?.[index] || '-'}</p>
                      <p><strong>{t("booking.loading_reference")}:</strong> {booking.loading_reffernce?.[index] || '-'}</p>
                      <p><strong>{t("booking.date")}:</strong> {booking.loading_date_time?.[index]}</p>
                    </div>
                  ))}
                </Card.Body>
              </Card>

              {/* معلومات التنزيل */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">
                  {t("booking.unloading_info")} ({booking.booking_to?.length || 0} {t('locations')})
                </Card.Header>
                <Card.Body>
                  {booking.booking_to?.map((address, index) => (
                    <div key={index} className="border p-3 mb-3">
                      <h6>{t('unloading_location')} {index + 1}:</h6>
                      <p><strong>{t("booking.company")}:</strong> {booking.unloading_company?.[index] || '-'}</p>
                      <p><strong>{t("booking.name")}:</strong> {booking.unloading_first_name?.[index]} {booking.unloading_last_name?.[index]}</p>
                      <p><strong>{t("booking.address")}:</strong> {booking.unloading_streat_no?.[index]}, {address}</p>
                      <p><strong>{t("booking.unloading_address_additional")}:</strong> {booking.unloading_address_additional?.[index]}</p>
                      <p><strong>{t("booking.unloading_postal")}:</strong> {booking.unloading_postal?.[index]}</p>
                      <p><strong>{t("booking.phone")}:</strong> {booking.unloading_phone?.[index] || '-'}</p>
                      <p><strong>{t("booking.unloading_reference")}:</strong> {booking.unloading_reffernce?.[index] || '-'}</p>
                      <p><strong>{t("booking.date")}:</strong> {booking.unloading_date_time?.[index]}</p>
                    </div>
                  ))}
                </Card.Body>
              </Card>

              {/* معلومات الشحنة */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">{t("booking.shipment_details")}</Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>{t("booking.item")}</th>
                          <th>{t("booking.quantity")}</th>
                          <th>{t("booking.dimensions")}</th>
                          <th>{t("booking.weight_per_stack")}</th>
                          <th>{t("booking.total_weight")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(booking.shipment_data || []).map((item, idx) => (
                          <tr key={idx}>
                            <td>{t(item.shipment_data) || 'Palette'}</td>
                            <td>{item.quantity || booking.quantity || 1}</td>
                            {item.shipment_data != "dokument" ? (
                              <>
                                <td>
                                  {item.length || 0} x {item.width || 0} x {item.height || 0} cm
                                </td>
                                <td>{formatNumber(item.weight)} kg</td>
                                <td>
                                  {formatNumber((item.quantity || booking.quantity || 1) * (item.weight || 0))} kg
                                </td>
                              </>
                            ) : (
                              <>
                                <td>-</td>
                                <td>-</td>
                                <td>-</td>
                              </>
                            )}
                            
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={4} className="fw-bold">{t("booking.total_weight")}</td>
                          <td className="fw-bold text-success">
                            {formatNumber(booking.total_weight)} kg
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4} className="fw-bold">{t("booking.total_distance")}</td>
                          <td className="fw-bold">
                            {booking.distance}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={4} className="fw-bold">{t("duration")}</td>
                          <td className="fw-bold">
                            {booking.total_duration}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={2} className="fw-bold">{t("vehicle_name")}</td>
                          <td colSpan={4} className="fw-bold">
                            {booking.vehicle_size}
                          </td>
                        </tr>
                        {booking.vehicle_id === 4 && (
                          <tr>
                            <td colSpan={4} className="fw-bold">{t("crane")}</td>
                            <td className="fw-bold">
                              {booking.crane}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>

              {/* 1. عرض التفاصيل الإضافية */}
              {renderAdditionalDetails()}

            </Col>

            <Col md={4}>
              {/* معلومات المستخدم */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center flex-wrap">
                  {t("booking.user_info")}
                  {booking.user?.id && (
                    <>
                      <div className="d-flex gap-3">
                        <button
                          className="btn btn-primary"
                          onClick={() => navigate(`/admin/customers/details/${booking.user.id}`)}
                        >
                          <FaUserAlt className="" />
                        </button>

                        <button
                          className="btn btn-success"
                          onClick={() => navigate(`/admin/client-list/chat-client/${booking.user.id}`)}
                        >
                          <FaFacebookMessenger className="" />
                        </button>
                      </div>
                    </>
                  )}
                </Card.Header>
                <Card.Body>
                  {booking.user ? (
                    <>
                      <p><strong>{t("user_name")}:</strong> {booking.user.user_name}</p>
                      <p><strong>{t("phone")}:</strong> {booking.user.user_phone}</p>
                      <p><strong>{t("user_email")}:</strong> {booking.user.user_email}</p>
                    </>
                  ) : (
                    <p className="text-muted">{t('no_user_found')}</p>
                  )}
                </Card.Body>
              </Card>

              {/* معلومات السائق */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center flex-wrap">
                  {t("booking.driver_info")}
                  
                  {booking.order && booking.order.driver_role === 'Driver' ? (
                    <>
                      <div className="d-flex gap-3">
                        <button
                          className="btn btn-primary"
                          onClick={() => navigate(`/admin/drivers/details/${booking.order.order_driver}`)}
                        >
                          <RiUserLocationFill className="" />
                        </button>
                        <button
                          className="btn btn-success"
                          onClick={() => navigate(`/admin/driver-list/chat-driver/${booking.order.order_driver}`)}
                        >
                          <FaFacebookMessenger className="me-1" />
                        </button>
                      </div>
                    </>
                  ) : booking.order && booking.order.driver_role === 'Driver Company' ? (
                    <>
                      <div className="d-flex gap-3">
                        <button
                          className="btn btn-primary"
                          onClick={() => navigate(`/admin/drivers-company/details/${booking.order.order_driver}`)}
                        >
                          <RiUserLocationFill className="" />
                        </button>
                        <button
                          className="btn btn-success"
                          onClick={() => navigate(`/admin/driver-company-list/chat-driver-company/${booking.order.order_driver}`)}
                        >
                          <FaFacebookMessenger className="me-1" />
                        </button>
                      </div>
                    </>
                  ) : null}
                </Card.Header>
                <Card.Body>
                  {booking.order ? (
                    <>
                      <p><strong>{t("driver_name")}:</strong> {booking.order.driver_name}</p>
                      <p><strong>{t("driver_role")}:</strong> {booking.order.driver_role}</p>
                      <p><strong>{t("phone")}:</strong> {booking.order.phone_number}</p>
                      <p><strong>{t("vehicle_type")}:</strong> {booking.order.vehicle_type}</p>
                      <p><strong>{t("vehicle_number")}:</strong> {booking.order.vehicle_number}</p>
                      <p><strong>{t("current_location")}:</strong> {booking.order.current_location}</p>
                      <p><strong>{t("status")}:</strong> {getStatusBadge(booking.order.status)}</p>
                    </>
                  ) : (
                    <p className="text-muted">{t('no_driver_assigned')}</p>
                  )}
                </Card.Body>
              </Card>

              {/* الخدمات الإضافية */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">{t("booking.extra_services")}</Card.Header>
                <Card.Body>
                  {booking.extra_service && booking.extra_service.length > 0 ? (
                    <ul className="list-unstyled">
                      {booking.extra_service.map((service, index) => (
                        <li key={index}>• {t(`${service}_services`)}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">{t("booking.no_service_selected")}</p>
                  )}
                </Card.Body>
              </Card>

              {/* 7. إصلاح مشكلة الـ Discount */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title d-flex justify-content-between align-items-center">
                  {t("booking.price")}

                  {/* <div className="d-flex align-items-center justify-content-center">
                    <Form.Check
                      type="switch"
                      checked={booking.hide_price}
                      onClick={handleHidePrice}
                      className="custom-switch"
                    />
                    <span className="toggle-label">
                      {booking.hide_price ? t('show') : t('hide')}
                    </span>
                  </div> */}

                  {booking.status === 'pending' && (
                    <Button 
                      variant={booking.hide_price ? "secondary" : "outline-info"}
                      onClick={handleHidePrice}
                      disabled={updating}
                      size="sm"
                    >
                      {booking.hide_price ? <FaEye className="me-1" /> : <FaEyeSlash className="me-1" />}
                      {booking.hide_price ? t("show") : t("hide")}
                    </Button>
                  )}

                </Card.Header>
                <Card.Body>
                  {booking.hide_price === 1 && (
                    <div className="mb-2">
                      <Alert variant="warning" className="m-3">
                        {t("booking.price_will_not_visible")}
                      </Alert>
                    </div>
                  )}
                  <div className="mb-2"><strong>{t("booking.net")}:</strong> {booking.sub_total} EUR</div>
                  <div className="mb-2"><strong>{t('Gesamt Bertrag')}</strong></div>
                  <div className="mb-2"><strong>{t("exkl. 19% MwSt. Brutto")}</strong> {booking.total} EUR</div>
                  <div className="mb-2"><strong>{t("booking.current_discount")}:</strong> {booking.discount_percentage || 0}%</div>
                  <hr />
                  <Form.Label className="fw-bold">{t("booking.update_discount")}</Form.Label>
                  <InputGroup className="mb-2">
                    <Form.Control
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(Number(e.target.value))}
                      min="0"
                      max="100"
                    />
                    <InputGroup.Text>%</InputGroup.Text>
                    <Button 
                      className="update-btn" 
                      onClick={handleUpdateDiscount}
                      disabled={updating}
                    >
                      {updating ? <Spinner size="sm" /> : t("booking.update")}
                    </Button>
                  </InputGroup>
                  <div className="bg-light p-3 rounded border">
                    <h5 className="mb-0">
                      <strong>{t("booking.total_after_discount")}:</strong> {booking.discounted_price} EUR
                    </h5>
                  </div>
                </Card.Body>
              </Card>

              {/* الملاحظات */}
              <Card className="mb-4 shadow-sm">
                <Card.Header className="fw-bold card-title">{t("booking.additional_details")}</Card.Header>
                <Card.Body>
                  <p><strong>{t("user_note")}:</strong> {booking.user_note}</p>
                  <p><strong>{t("booking_message")}:</strong> {booking.message}</p>

                  <Form>
                    <Form.Group controlId="additionalDetails">
                      <Form.Label>{t("booking.notes_label")}</Form.Label>
                      <Form.Control 
                        as="textarea" 
                        rows={4} 
                        placeholder={t("booking.notes_placeholder")}
                        value={notizen}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </Form.Group>
                    <div className="mt-3 d-flex justify-content-end">
                      <Button className="update-btn" onClick={handleSendMessage}>
                        {t("booking.submit_notes")}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>

              {/* السائق */}
              {(booking.status === 'pending' || booking.status === 'approved') && (
                <Card className="mb-4 shadow-sm">
                  <Card.Header className="fw-bold card-title">{t("booking.driver_assignment")}</Card.Header>
                  
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('select_driver')}</Form.Label>
                      <div className="d-grid">
                        <Button variant="outline-primary" onClick={() => setShowDriversModal(true)}>
                          <FaTruck /> {t('select_driver')}
                        </Button>
                      </div>
                      {drivers.id && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <strong>{t('selected_driver')}: </strong>
                          {drivers.firstname} {drivers.lastname}
                        </div>
                      )}
                    </Form.Group>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        </Tab>

        <Tab eventKey="logs" title={t("admin_logs")}>
          <div className="table-container shadow-sm rounded">
            <div className="table-responsive">
              <Table bordered hover className="custom-table mb-0 text-center">
                <thead className="table-dark">
                  <tr>
                    <th>{t("admin_name")}</th>
                    <th>{t("admin_email")}</th>
                    <th>{t("action")}</th>
                    <th>{t("description")}</th>
                    <th>{t("created_at")}</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.admin_logs && booking.admin_logs.map((entry) => (
                    <tr key={entry.id}>
                      <td>{entry.admin_name || "N/A"}</td>
                      <td>{entry.admin_email || "N/A"}</td>
                      <td>{entry.action}</td>
                      <td>{entry.description}</td>
                      <td>{new Date(entry.created_on).toLocaleString('de-DE')}</td>
                      {isSuperAdmin && (
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => {
                              setDeleteLogId(entry.id);
                              setShowDeleteLog(true);
                            }}
                            title={t("delete")}
                            disabled={loading}
                          >
                            <FaTrashAlt />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* Reject Modal */}
      <Modal show={showRejectModal} onHide={() => setShowRejectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("booking.reject_booking")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{t("booking.reject_reason")} *</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("booking.enter_reject_reason")}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRejectModal(false)}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={handleReject} disabled={updating}>
            {updating ? <Spinner size="sm" /> : t("booking.confirm_reject")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Log Modal */}
      <Modal show={showDeleteLog} onHide={() => setShowDeleteLog(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("confirm_delete_log")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteLog(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDeleteLog} disabled={loading}>
            {loading ? t("deleting") : t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal لاختيار السائق */}
      <Modal show={showDriversModal} onHide={() => setShowDriversModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('select_driver')}</Modal.Title>
        </Modal.Header>

        {/* Search Bar */}
        <Row className="m-3">
          <Col md={8}>
            <InputGroup>
              <InputGroup.Text><FaSearch /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder={t('search_by_email')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <Button 
                  variant="outline-secondary" 
                  onClick={clearSearch}
                  title={t("reset_search")}
                >
                  ✕
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>

        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner />
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('email')}</th>
                    <th>{t('company')}</th>
                    <th>{t('vehicle_type')}</th>
                    <th>{t('action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {drivers.map(driver => (
                    <tr key={driver.id}>
                      <td>{driver.firstname} {driver.lastname}</td>
                      <td>
                        {searchTerm ? (
                          <span 
                            dangerouslySetInnerHTML={{
                              __html: driver.email?.replace(
                                new RegExp(searchTerm, 'gi'), 
                                match => `<mark class="bg-warning">${match}</mark>`
                              )
                            }} 
                          />
                        ) : (
                          driver.email
                        )}
                      </td>
                      <td>{driver.company_name}</td>
                      <td>{driver.vehicle_type}</td>
                      <td>
                        <Button
                          size="sm"
                          variant={booking.customer_id === driver.id ? 'success' : 'outline-primary'}
                          onClick={() => selectDriver(driver)}
                        >
                          {booking.customer_id === driver.id ? t('selected') : t('select')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Add/Edit Modal */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editData ? t('edit') : t('add')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="mb-3 gy-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("rechnungsnummer")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="invoice_num"
                    value={form.invoice_num}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("customer_num")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="customer_num"
                    value={form.customer_num}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("payment_term")}</Form.Label>
                  <Form.Control
                    type="number"
                    name="payment_term"
                    value={form.payment_term}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t("created_at")}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="created_at"
                    value={form.created_at}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? t('saving') : t('save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Invoice View Modal */}
      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton className="bg-dark text-white">
          <Modal.Title>{t("viewInvoice")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoice && (
            <div className="text-center">
              <h5 className="mb-3">
                {t("booking.Invoice")}: {safeDisplay(selectedInvoice.booking_id)}
              </h5>

              <div
                style={{
                  height: "500px",
                  border: "1px solid #ddd",
                  borderRadius: "5px",
                }}
              >
                <iframe
                  src={selectedInvoice.invoice_path}
                  title={`Invoice-${selectedInvoice.booking_id}`}
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              </div>

              <div className="mt-3 p-3 bg-light rounded">
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>{t("date")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.date)}
                    </p>
                    <p>
                      <strong>{t("sub_total")}:</strong>{" "}
                      {formatCurrency(selectedInvoice.sub_total)}
                    </p>
                    <p>
                      <strong>{t("customer_num")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.customer_num)}
                    </p>
                    <p>
                      <strong>{t("rechnungsnummer")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.invoice_num)}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>
                        {t("payment_status")}:
                      </strong>{" "}
                      {getStatusBadge(selectedInvoice.payment_status)}
                    </p>
                    <p>
                      <strong>{t("total")}:</strong>{" "}
                      {formatCurrency(selectedInvoice.total)}
                    </p>
                    <p>
                      <strong>{t("payment_term")}:</strong>{" "}
                      {safeDisplay(selectedInvoice.payment_term)} {t("days")}
                    </p>
                  </Col>
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowInvoiceModal(false)}
          >
            {t("close")}
          </Button>
          <Button
            variant="primary"
            onClick={() => selectedInvoice && handleDownload(selectedInvoice)}
            disabled={!selectedInvoice?.invoice_path}
          >
            <FaDownload className="me-2" />
            {t("booking.download_pdf")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Cancel Invoice Confirmation Modal */}
      <Modal show={showCancelInvoiceModal} onHide={() => setShowCancelInvoiceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('cancel_invoice')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('confirm_cancel_invoice')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelInvoiceModal(false)}>
            {t('cancel')}
          </Button>
          <Button variant="warning" onClick={handleCancelInvoiceConfirm}>
            {t('confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
